import React, { useEffect, useState } from "react";
import Countdown from "react-countdown";
import {
  FaCreditCard,
  FaPaperPlane,
  FaMapMarkerAlt,
  FaClock,
  FaBuilding,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/app/global-components/card";
import { isEmpty } from "lodash";
import "./styles.css";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import useOffer from "@/hooks/useTender";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";

interface Opportunity {
  city: string;
  region: string;
  categories: any;
  endDate: string | number | Date | undefined;
  id: number;
  title: string;
  category: string;
  location: string;
  status: string;
  remainingTime: number;
  companyId: string;
  company?: any;
}

interface OpportunityCardProps {
  opportunity: Opportunity;
}
interface Region {
  region_id: number;
  name_ar: string;
}
interface City {
  city_id: number;
  name_ar: string;
}
interface CategoryOption {
  value: string;
  label: string;
}

const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity }) => {
  const { locale } = useLocale();
  const [regions, setRegions] = useState<CategoryOption[]>([]);
  const [cities, setCities] = useState<CategoryOption[]>([]);
  const getCity = (value: string) => {
    const city = cities.find(
      (item) => parseInt(item.value) === parseInt(value)
    );
    return city ? city.label : "";
  };
  const getRegion = (value: string) => {
    const region = regions.find(
      (item) => parseInt(item.value) === parseInt(value)
    );
    return region ? region.label : "";
  };
  useEffect(() => {
    setCities(
      citiesData.map((city: City) => ({
        value: city.city_id.toString(),
        label: city.name_ar,
      }))
    );
    setRegions(
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );
  }, []);
  const { t } = useTranslate(locale);
  const statuses = [
    {
      label: t("status.planning"),
      name: "Planning",
      backgroundColor: "#fef3c7",
      color: "#92400e",
      borderColor: "#fde68a",
    },
    {
      label: t("status.planning"),
      name: "Pending",
      backgroundColor: "#fef3c7",
      color: "#92400e",
      borderColor: "#fde68a",
    },
    {
      label: t("status.opened"),
      name: "Opened",
      backgroundColor: "#dbeafe",
      color: "#1e40af",
      borderColor: "#93c5fd",
    },
    {
      label: t("status.closed"),
      name: "Closed",
      backgroundColor: "#f3f4f6",
      color: "#374151",
      borderColor: "#d1d5db",
    },
    {
      label: t("status.accepted"),
      name: "Accepted",
      backgroundColor: "#d1fae5",
      color: "#065f46",
      borderColor: "#a7f3d0",
    },
    {
      label: t("status.payment"),
      name: "Payment",
      backgroundColor: "#fef3c7",
      color: "#92400e",
      borderColor: "#fde68a",
    },
    {
      label: t("status.paymentConfirmation"),
      name: "PaymentConfirmation",
      backgroundColor: "#fed7aa",
      color: "#c2410c",
      borderColor: "#fdba74",
    },
    {
      label: t("status.sendingLabel"),
      name: "Sending",
      backgroundColor: "#e9d5ff",
      color: "#7c3aed",
      borderColor: "#c4b5fd",
    },
    {
      label: t("status.receiving"),
      name: "Receiving",
      backgroundColor: "#dcfce7",
      color: "#166534",
      borderColor: "#bbf7d0",
    },
    {
      label: t("status.finished"),
      name: "Finished",
      backgroundColor: "#fecaca",
      color: "#dc2626",
      borderColor: "#fca5a5",
    },
    {
      label: t("status.canceled"),
      name: "Canceled",
      backgroundColor: "#fee2e2",
      color: "#dc2626",
      borderColor: "#fca5a5",
    },
  ];
  const [status, setStatus] = useState<string>(opportunity.status);
  const { sendOffer } = useOffer();
  const navigate = useNavigate();

  const getStatusLabel = (status: string) => {
    const statusProps = statuses.find((s) => s.name === status) || {
      label: t("status.unknown"),
      backgroundColor: "#f9fafb",
      color: "#6b7280",
      borderColor: "#e5e7eb",
    };
    return statusProps;
  };

  const handleSendOpportunity = (e: React.MouseEvent) => {
    e.stopPropagation();
    sendOffer(opportunity.id.toString(), "Opened");
  };

  const handleCountdownComplete = () => {
    setStatus("ended");
  };

  const statusConfig = getStatusLabel(opportunity.status);

  return (
    <Card
      key={opportunity.id}
      onClick={() => navigate(`${routes.vuetender}/${opportunity.id}`)}
      className="bg-white border border-gray-200 rounded-xl transition-all duration-300 cursor-pointer overflow-hidden group"
    >
      {/* Header */}
      <CardHeader className="p-6 pb-4">
        <div className="flex justify-between items-start mb-4">
          <div
            className="px-3 py-1 rounded-full text-xs font-medium border"
            style={{
              backgroundColor: statusConfig.backgroundColor,
              color: statusConfig.color,
              borderColor: statusConfig.borderColor,
            }}
          >
            {status === "ended" ? t("time.ended") : statusConfig.label}
          </div>
          <div className="text-gray-400 group-hover:text-gray-600 transition-colors">
            <FaCreditCard size={20} />
          </div>
        </div>

        {/* Company Number */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
          <FaBuilding className="w-4 h-4" />
          <span className="font-medium">
            {opportunity.company?.CompanyNr}
          </span>
        </div>
      </CardHeader>

      {/* Content */}
      <CardContent className="px-6 pb-4">
        <CardTitle className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {opportunity.title}
        </CardTitle>

        {/* Categories */}
        {!isEmpty(opportunity.categories) && (
          <div className="mb-3">
            <div className="text-sm text-gray-600 line-clamp-2">
              {opportunity.categories
                .slice(0, 3)
                .map((item: any, index: number) => (
                  <span
                    key={index}
                    className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-xs mr-2 mb-1"
                  >
                    {item}
                  </span>
                ))}
              {opportunity.categories.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{opportunity.categories.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Location */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <FaMapMarkerAlt className="w-4 h-4 text-gray-400" />
          <span>
            {getRegion(opportunity.region)}, {getCity(opportunity.city)}
          </span>
        </div>
      </CardContent>

      {/* Footer */}
      <CardFooter className="px-6 py-4 bg-gray-50 border-t border-gray-100">
        <div className="w-full">
          {["Planning", "Pending"].includes(opportunity.status) ? (
            <button
              className="w-full bg-zinc-900 text-sm text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 font-medium"
              onClick={handleSendOpportunity}
            >
              <FaPaperPlane className="w-4 h-4" />
              {t("actions.sendOpportunity")}
            </button>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-700 bg-white px-4 py-3 rounded-lg border border-gray-200">
              <FaClock className="w-4 h-4 text-gray-400" />
              <Countdown
                date={opportunity.endDate}
                onComplete={handleCountdownComplete}
                renderer={({ days, hours, minutes, seconds, completed }) => {
                  if (completed) {
                    return (
                      <span className="font-medium text-red-600">
                        {t("time.ended")}
                      </span>
                    );
                  }
                  if (days > 0) {
                    return (
                      <span className="font-medium">
                        {days + 1} {t("time.day")}
                      </span>
                    );
                  }
                  return (
                    <span className="font-medium">
                      {hours}:{minutes.toString().padStart(2, "0")}:
                      {seconds.toString().padStart(2, "0")}
                    </span>
                  );
                }}
              />
            </div>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export default OpportunityCard;
